import Footer from '@/components/Footer';
import { Icons } from '@/components/icons';
import { Loadable } from '@/components/Loadable';
import { Button } from '@/components/primitives/Button';
import { CardPrimitiveRoot } from '@/components/primitives/Card';
import { Typography } from '@/components/primitives/Typography';
import BaseOfferCard from '@/components/purchase/card/BaseOfferCard';
import { tenantSelector } from '@/features/configuration/configurationSelector';
import { purchaseFlowBookingCreationLoadingSelector } from '@/features/loading/loadingSelectors';
import { updateSelectedNonTripOffer } from '@/features/purchase/purchaseActions';
import { nonTripOffersSelector } from '@/features/purchase/purchaseSelectors';
import { useBookingCreation } from '@/hooks/useBookingCreation';
import { TransTenantSpecific } from '@/i18n/trans/tenantSpecific';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { SearchNonTripOfferDTO } from '@/types/dto/agent';
import { breakpoints } from '@/utils/breakpoints';
import { cn } from '@/utils/cn';
import { getTenantConfiguration } from '@/utils/tenant';
import { type FC, useCallback, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate } from 'react-router-dom';

interface NonTripOfferListProps {}

const NonTripOfferList: FC<NonTripOfferListProps> = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loading = useSelector(purchaseFlowBookingCreationLoadingSelector);
  const tenant = useSelector(tenantSelector);
  const { nonTripOffers, selectedNonTripOffer } = useSelector(
    nonTripOffersSelector
  );
  const configuration = getTenantConfiguration(tenant.value);
  const isLaptopOrBigger = useMediaQuery({
    minWidth: breakpoints.laptop,
  });
  const { handleCreatingAndFetchingNonTripOfferBooking } = useBookingCreation();

  const getOffersColumns = useCallback(
    (offers: Array<SearchNonTripOfferDTO> | undefined) => {
      if (!offers?.length) {
        return [[], []];
      }

      const sortedOffers = [...offers].sort(
        (a, b) =>
          (a.offerSummary?.minimalPrice?.amount ?? 0) -
          (b.offerSummary?.minimalPrice?.amount ?? 0)
      );

      const firstColumn: Array<SearchNonTripOfferDTO> = [];
      const secondColumn: Array<SearchNonTripOfferDTO> = [];

      sortedOffers.forEach((offer, index) => {
        if (index % 2 === 0) {
          firstColumn.push(offer);
        } else {
          secondColumn.push(offer);
        }
      });

      return [firstColumn, secondColumn];
    },
    []
  );

  useEffect(() => {
    if (!nonTripOffers) {
      navigate('/purchase');
    }
  }, [navigate, nonTripOffers]);

  useEffect(() => {
    if (nonTripOffers) {
      dispatch(updateSelectedNonTripOffer(nonTripOffers[0]));
    }
  }, [dispatch, nonTripOffers]);

  const renderOfferCard = (
    nonTripOffer: SearchNonTripOfferDTO,
    isSelected: boolean
  ) => {
    const condition = nonTripOffer.admissionOfferParts?.[0].conditions?.[0];
    const minimalPrice = nonTripOffer.offerSummary?.minimalPrice;
    const appliedReduction =
      nonTripOffer?.admissionOfferParts?.[0]?.appliedReductions?.[0];

    return (
      <BaseOfferCard
        key={nonTripOffer.id}
        title={condition?.shortDescription ?? condition?.description ?? ''}
        amount={minimalPrice?.amount || 0}
        isSelected={isSelected}
        onSelect={() => dispatch(updateSelectedNonTripOffer(nonTripOffer))}
        description={condition?.description}
        isRefundable={
          nonTripOffer.admissionOfferParts?.[0].refundable === 'YES'
        }
        isChangeable={
          nonTripOffer.admissionOfferParts?.[0].exchangeable === 'YES'
        }
        appliedReduction={appliedReduction}
        testId="select-non-trip-offer"
      />
    );
  };

  const [firstColumn, secondColumn] = getOffersColumns(nonTripOffers);

  return (
    <Loadable loading={loading} overlay>
      <div className="mx-auto w-full max-w-[800px] p-0 pb-24 laptop:p-4 laptop:pb-24">
        <Typography
          asChild
          variant={isLaptopOrBigger ? 'heading1' : 'heading2'}
          className="p-4 laptop:p-0 laptop:pb-4"
        >
          <h1>{<TransText i18nKey="selectTravelPass" />}</h1>
        </Typography>

        <>
          <CardPrimitiveRoot
            className={cn(
              'flex flex-col gap-4 rounded-none border-none p-4 shadow-none laptop:flex-none laptop:flex-grow laptop:flex-row laptop:rounded-lg laptop:border-solid laptop:shadow-sm'
            )}
          >
            {isLaptopOrBigger ? (
              <>
                <div className="flex w-full flex-col gap-3">
                  {firstColumn?.map((nonTripOffer) =>
                    renderOfferCard(
                      nonTripOffer,
                      Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                    )
                  )}
                </div>
                <div className="flex w-full flex-col gap-3">
                  {secondColumn?.map((nonTripOffer) =>
                    renderOfferCard(
                      nonTripOffer,
                      Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                    )
                  )}
                </div>
              </>
            ) : (
              nonTripOffers?.map((nonTripOffer) =>
                renderOfferCard(
                  nonTripOffer,
                  Boolean(selectedNonTripOffer?.id === nonTripOffer.id)
                )
              )
            )}
          </CardPrimitiveRoot>
          {configuration?.showTravelPassDisclaimer && (
            <div className="prose flex items-center gap-1.5 px-4 pb-2 prose-p:m-0 prose-a:text-blue-600 prose-a:no-underline laptop:px-0 laptop:pb-0 laptop:pt-4">
              <Icons.infoCircle />
              <Typography variant="body2" className="text-center">
                <TransTenantSpecific i18nKey="travelPassDisclaimer" />
              </Typography>
            </div>
          )}
        </>
      </div>
      <Footer
        actionButtons={
          <Button
            fullWidth
            size={isLaptopOrBigger ? 'medium' : 'large'}
            data-testid="footer-continue-button"
            className="rounded-lg laptop:w-auto"
            disabled={!selectedNonTripOffer}
            onClick={handleCreatingAndFetchingNonTripOfferBooking}
            variant="cta"
          >
            <TransText i18nKey="continue" />
            <Icons.arrowRight />
          </Button>
        }
      />
    </Loadable>
  );
};

export default NonTripOfferList;
